import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ChevronLeft, Settings, LogOut, User } from 'lucide-react';
import { useAuth } from '../../App';

/**
 * Composant Header unifié pour toutes les pages de l'application.
 * Garantit une cohérence visuelle avec :
 * - Logo agrandi (h-16) à gauche
 * - Lien de retour au dashboard harmonisé
 * - Actions utilisateur à droite
 */
const PageHeader = ({ 
  showBackButton = true,
  backTo = '/dashboard',
  backLabel = 'Dashboard',
  title = null,
  titleIcon: TitleIcon = null,
  rightContent = null,
  showUserActions = false,
  variant = 'default' // 'default' | 'minimal' | 'tool'
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 sm:h-24">
          {/* Left section - Logo + Back button */}
          <div className="flex items-center gap-4">
            {showBackButton && (
              <Link to={backTo}>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5"
                  data-testid="header-back-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {backLabel}
                </Button>
              </Link>
            )}
            
            {/* Logo - taille uniforme h-16 */}
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="/logo-horizontal.png" 
                alt="10 Millions Investor" 
                className="h-16 sm:h-20 w-auto"
                data-testid="header-logo"
              />
            </Link>

            {/* Optional page title */}
            {title && (
              <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-slate-200">
                {TitleIcon && <TitleIcon className="h-5 w-5 text-[#00B050]" />}
                <h1 className="font-semibold text-slate-900">{title}</h1>
              </div>
            )}
          </div>

          {/* Right section */}
          <div className="flex items-center gap-3">
            {rightContent}
            
            {showUserActions && (
              <>
                <Link to="/profile">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="hidden md:flex text-slate-600 hover:text-[#0D47A1]"
                    data-testid="header-profile-btn"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Profil
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout}
                  className="text-slate-600 hover:text-red-600"
                  data-testid="header-logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  <span className="hidden md:inline">Déconnexion</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
